import { useState, useRef, useEffect, useCallback } from "react";
import { Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { RecordingBar } from "./RecordingBar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface VoiceButtonProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  /** Render recording bar externally */
  renderExternal?: boolean;
  /** Called when recording starts */
  onRecordingStart?: () => void;
  /** Called when recording ends */
  onRecordingEnd?: () => void;
}

// Type definitions for Web Speech API
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export interface VoiceRecordingState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  liveTranscript: string;
  onCancel: () => void;
  onSend: () => void;
  onTogglePause: () => void;
}

export const VoiceButton = ({ 
  onTranscript, 
  disabled,
  renderExternal = false,
  onRecordingStart,
  onRecordingEnd,
}: VoiceButtonProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState("");
  const haptic = useHapticFeedback();
  const { toast } = useToast();

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const durationInterval = useRef<NodeJS.Timeout>();
  const finalTranscriptRef = useRef("");
  const isPausedRef = useRef(false);
  const resumeRequestedRef = useRef(false);
  const shouldBeRecordingRef = useRef(false);
  const restartAttemptsRef = useRef(0);
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // MediaRecorder fallback (Android PWA)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaChunksRef = useRef<BlobPart[]>([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionProgress, setTranscriptionProgress] = useState(0);
  const [transcriptionFailed, setTranscriptionFailed] = useState(false);
  const transcriptionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transcriptionProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check browser support
  const getSpeechRecognition = useCallback(() => {
    return window.SpeechRecognition || window.webkitSpeechRecognition;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
      }
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }

      // MediaRecorder cleanup (Android PWA fallback)
      if (mediaRecorderRef.current) {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          // ignore
        }
        mediaRecorderRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
    };
  }, []);

  const isAndroidPwa = useCallback(() => {
    try {
      const isAndroid = /Android/i.test(navigator.userAgent);
      // PWA detection isn't perfect, but this covers the common case.
      const isStandalone =
        (typeof window !== "undefined" && (window.navigator as any)?.standalone === true) ||
        (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches);
      return isAndroid && isStandalone;
    } catch {
      return false;
    }
  }, []);

  // Detect iOS (Safari)
  const isIOS = useCallback(() => {
    try {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    } catch {
      return false;
    }
  }, []);

  // Check if we should use MediaRecorder fallback
  // Per debug guide: iOS uses Web Speech API (more reliable), only Android PWA uses MediaRecorder
  const shouldUseMediaRecorderFallback = useCallback(() => {
    // Only Android PWA uses MediaRecorder fallback
    if (isAndroidPwa()) {
      console.log("🤖 Android PWA detected - using MediaRecorder");
      return true;
    }
    
    // iOS uses Web Speech API (more reliable than MediaRecorder on iOS)
    if (isIOS()) {
      console.log("🍎 iOS detected - using Web Speech API (more reliable)");
      return false;
    }
    
    return false;
  }, [isAndroidPwa, isIOS]);

  const ensureMicrophonePermission = useCallback(async () => {
    // Request explicit mic permission - important for iOS Safari and Android PWAs
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release immediately; SpeechRecognition will capture separately.
      stream.getTracks().forEach((t) => t.stop());
    } catch (error) {
      console.error("Microphone permission denied:", error);
      throw error; // Propagate so caller knows permission failed
    }
  }, []);

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    // Chunk to avoid call stack limits on large recordings
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const pickBestMimeType = () => {
    // iOS Safari only supports mp4, so prioritize it on iOS
    const isiOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    const candidates = isiOSDevice 
      ? [
          // iOS Safari supports these formats
          "audio/mp4",
          "audio/aac",
          "audio/wav",
        ]
      : [
          // Android/Chrome supports these
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/mp4",
          "audio/wav",
        ];
    
    const supported = candidates.find((t) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(t));
    console.log("Selected audio MIME type:", supported || "fallback");
    return supported || (isiOSDevice ? "audio/mp4" : "audio/webm");
  };

  const startMediaRecording = useCallback(async () => {
    console.log("🎙️ Starting MediaRecorder fallback...");
    
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      console.error("❌ MediaRecorder not supported");
      toast({
        title: "Voice input not supported",
        description: "This device/browser doesn't support audio recording.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("🔐 Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log("✅ Microphone access granted, tracks:", stream.getAudioTracks().length);
      mediaStreamRef.current = stream;
      mediaChunksRef.current = [];

      const mimeType = pickBestMimeType();
      console.log("🎵 Creating MediaRecorder with mimeType:", mimeType);
      
      // On Android, don't pass mimeType if not supported - let browser choose
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType });
      } catch (e) {
        console.warn("⚠️ Failed with mimeType, trying without:", e);
        recorder = new MediaRecorder(stream);
      }
      mediaRecorderRef.current = recorder;
      console.log("▶️ MediaRecorder created, state:", recorder.state);

      shouldBeRecordingRef.current = true;
      setIsPaused(false);
      isPausedRef.current = false;
      setLiveTranscript("");

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          mediaChunksRef.current.push(e.data);
        }
      };

      recorder.onstart = () => {
        setIsRecording(true);
        setRecordingDuration(0);
        haptic.success();
        onRecordingStart?.();

        if (!durationInterval.current) {
          durationInterval.current = setInterval(() => {
            setRecordingDuration((prev) => prev + 1);
          }, 1000);
        }
      };

      recorder.onstop = async () => {
        console.log("⏹️ MediaRecorder stopped, chunks collected:", mediaChunksRef.current.length);
        
        // Always stop tracks
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((t) => t.stop());
          mediaStreamRef.current = null;
        }

        // If cancelled, nothing to do
        if (!shouldBeRecordingRef.current) {
          console.log("🚫 Recording was cancelled, skipping transcription");
          return;
        }

        // Use the actual mimeType from recorder if available
        const actualMimeType = recorder.mimeType || mimeType;
        console.log("📦 Creating blob with mimeType:", actualMimeType);
        
        const blob = new Blob(mediaChunksRef.current, { type: actualMimeType });
        mediaChunksRef.current = [];
        console.log("📦 Blob created, size:", blob.size, "bytes");

        if (!blob.size) {
          console.warn("❌ Audio too small: 0 bytes");
          toast({
            title: "No audio captured",
            description: "Please try again.",
            variant: "default",
          });
          return;
        }

        // Validate audio size (minimum ~1 second of audio)
        if (blob.size < 1000) {
          console.warn("❌ Audio too small:", blob.size, "bytes");
          toast({
            title: "Recording too short",
            description: "Please speak for at least 1 second.",
            variant: "default",
          });
          return;
        }

        console.log("🔍 Validating audio quality...");

        try {
          setIsTranscribing(true);
          setTranscriptionProgress(0);
          setTranscriptionFailed(false);
          
          // Start progress animation (fake progress for UX)
          transcriptionProgressRef.current = setInterval(() => {
            setTranscriptionProgress((prev) => {
              // Slow down as we approach 90%
              if (prev < 30) return prev + 8;
              if (prev < 60) return prev + 4;
              if (prev < 85) return prev + 2;
              return prev + 0.5;
            });
          }, 200);
          
          // Set 30-second timeout
          transcriptionTimeoutRef.current = setTimeout(() => {
            console.warn("❌ Transcription timeout reached");
            if (transcriptionProgressRef.current) {
              clearInterval(transcriptionProgressRef.current);
              transcriptionProgressRef.current = null;
            }
            setIsTranscribing(false);
            setTranscriptionProgress(0);
            setTranscriptionFailed(true);
            toast({
              title: "Transcription timed out",
              description: "The server took too long. Please try again.",
              variant: "destructive",
            });
          }, 30000);
          
          console.log("✅ Audio valid, converting to base64...");
          const base64 = arrayBufferToBase64(await blob.arrayBuffer());
          console.log("🚀 Sending to transcribe-audio edge function...");
          console.log("   Base64 length:", base64.length, "MIME:", actualMimeType);
          
          const { data, error } = await supabase.functions.invoke("transcribe-audio", {
            body: { audio: base64, mimeType: actualMimeType },
          });

          if (error) {
            console.error("❌ Transcription error:", error);
            throw error;
          }
          
          const text = (data as any)?.text?.trim?.() ? (data as any).text.trim() : "";
          console.log("✅ Transcription successful:", text.substring(0, 50) + (text.length > 50 ? "..." : ""));

          // Complete progress
          setTranscriptionProgress(100);

          if (text) {
            onTranscript(text);
            haptic.success();
          } else {
            console.warn("⚠️ Empty transcription result");
            toast({
              title: "Couldn't understand audio",
              description: "Try speaking a bit closer to the microphone.",
              variant: "default",
            });
          }
        } catch (e: any) {
          console.error("❌ Transcription failed:", e);
          // Extract error message from response if available
          const errorMessage = e?.message || e?.error || "Unknown error";
          const detailMessage = typeof e === 'object' && e?.context?.body 
            ? JSON.parse(e.context.body)?.error 
            : null;
          
          setTranscriptionFailed(true);
          
          toast({
            title: "Transcription issue",
            description: detailMessage || errorMessage || "Please try again.",
            variant: "destructive",
          });
        } finally {
          // Clear timeout and progress interval
          if (transcriptionTimeoutRef.current) {
            clearTimeout(transcriptionTimeoutRef.current);
            transcriptionTimeoutRef.current = null;
          }
          if (transcriptionProgressRef.current) {
            clearInterval(transcriptionProgressRef.current);
            transcriptionProgressRef.current = null;
          }
          // Small delay to show 100% before hiding
          setTimeout(() => {
            setIsTranscribing(false);
            setTranscriptionProgress(0);
          }, 200);
        }
      };

      recorder.start(250);
    } catch (error) {
      console.error("Failed to start audio recording:", error);
      toast({
        title: "Voice input failed",
        description: "Could not access the microphone.",
        variant: "destructive",
      });
    }
  }, [haptic, onRecordingStart, onTranscript, toast]);

  const startRecording = useCallback(async () => {
    const SpeechRecognitionAPI = getSpeechRecognition();

    // Use MediaRecorder fallback only for Android PWA
    if (shouldUseMediaRecorderFallback()) {
      await startMediaRecording();
      return;
    }
    
    // Check if Web Speech API is available
    if (!SpeechRecognitionAPI) {
      console.error("❌ Web Speech API not available");
      toast({
        title: "Voice input not supported",
        description: "Your browser doesn't support voice input. Try Chrome, Safari, or Edge.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("🎤 Starting Web Speech API...");
      
      // Make mic permission explicit
      await ensureMicrophonePermission();
      console.log("✅ Microphone permission granted");

      shouldBeRecordingRef.current = true;
      restartAttemptsRef.current = 0;

      const recognition = new SpeechRecognitionAPI();
      recognitionRef.current = recognition;
      
      // Configure
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = navigator.language || 'en-US';
      console.log("🌐 Language set to:", recognition.lang);

      // Reset state
      finalTranscriptRef.current = "";
      setLiveTranscript("");
      setIsPaused(false);
      isPausedRef.current = false;
      resumeRequestedRef.current = false;

      recognition.onstart = () => {
        console.log("▶️ Speech recognition started");
        const isResume = resumeRequestedRef.current;
        resumeRequestedRef.current = false;

        setIsRecording(true);

        if (!isResume) {
          setRecordingDuration(0);
          haptic.success();
          onRecordingStart?.();
        }

        // Start (or resume) duration timer
        if (!durationInterval.current) {
          durationInterval.current = setInterval(() => {
            setRecordingDuration((prev) => prev + 1);
          }, 1000);
        }
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interimTranscript = "";
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            console.log("✅ Final transcript chunk:", result[0].transcript);
            finalTranscriptRef.current += result[0].transcript + " ";
          } else {
            interimTranscript += result[0].transcript;
          }
        }
        
        // Update live transcript (final + interim)
        setLiveTranscript(finalTranscriptRef.current + interimTranscript);
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error("❌ Speech recognition error:", event.error);
        
        if (event.error === 'not-allowed') {
          toast({
            title: "Microphone access denied",
            description: "Please allow microphone access in your browser settings.",
            variant: "destructive",
          });
        } else if (event.error === 'no-speech') {
          // Don't show error for no speech, just end gracefully
        } else if (event.error !== 'aborted') {
          toast({
            title: "Voice input error",
            description: "Could not recognize speech. Please try again.",
            variant: "destructive",
          });
        }
        
        stopRecording(true);
      };

      recognition.onend = () => {
        console.log("⏹️ Speech recognition ended");
        // If we're "paused", Web Speech fires onend after stop(); we must *not* teardown UI/state.
        if (isPausedRef.current) return;

        // Log the final transcript being sent
        const transcript = finalTranscriptRef.current.trim();
        if (transcript) {
          console.log("✅ Sending transcript:", transcript.substring(0, 50) + (transcript.length > 50 ? "..." : ""));
        }

        // Cleanup
        if (durationInterval.current) {
          clearInterval(durationInterval.current);
          durationInterval.current = undefined;
        }
        setIsRecording(false);
        setRecordingDuration(0);
        setLiveTranscript("");
        onRecordingEnd?.();
      };

      recognition.start();

    } catch (error) {
      console.error("Failed to start speech recognition:", error);
      haptic.error();
      toast({
        title: "Voice input failed",
        description: "Could not start voice recognition. Please try again.",
        variant: "destructive",
      });
    }
  }, [ensureMicrophonePermission, getSpeechRecognition, haptic, shouldUseMediaRecorderFallback, onRecordingEnd, onRecordingStart, startMediaRecording, toast]);

  const stopRecording = useCallback((cancelled = false) => {
    // MediaRecorder path
    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;

      // Mark whether we intend to process the recording.
      shouldBeRecordingRef.current = !cancelled;

      if (durationInterval.current) {
        clearInterval(durationInterval.current);
        durationInterval.current = undefined;
      }

      setIsRecording(false);
      setIsPaused(false);
      setRecordingDuration(0);
      setLiveTranscript("");
      onRecordingEnd?.();

      try {
        recorder.stop();
      } catch {
        // ignore
      }
      mediaRecorderRef.current = null;
      return;
    }

    // SpeechRecognition path
    shouldBeRecordingRef.current = false;
    restartAttemptsRef.current = 0;
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }

    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = undefined;
    }

    if (recognitionRef.current) {
      if (cancelled) {
        finalTranscriptRef.current = "";
        haptic.error();
        recognitionRef.current.abort();
      } else {
        const transcript = finalTranscriptRef.current.trim() || liveTranscript.trim();
        if (transcript) {
          onTranscript(transcript);
          haptic.success();
        } else {
          toast({
            title: "No speech detected",
            description: "Please try speaking more clearly.",
            variant: "default",
          });
        }
        finalTranscriptRef.current = "";
        recognitionRef.current.abort();
      }
    }

    setIsRecording(false);
    setIsPaused(false);
    setRecordingDuration(0);
    setLiveTranscript("");
    onRecordingEnd?.();
  }, [haptic, liveTranscript, onRecordingEnd, onTranscript, toast]);

  const togglePause = useCallback(() => {
    // MediaRecorder pause/resume
    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;
      haptic.light();

      if (isPaused) {
        try {
          recorder.resume();
        } catch {
          // ignore
        }
        isPausedRef.current = false;
        setIsPaused(false);
        shouldBeRecordingRef.current = true;

        if (!durationInterval.current) {
          durationInterval.current = setInterval(() => {
            setRecordingDuration((prev) => prev + 1);
          }, 1000);
        }
        return;
      }

      try {
        recorder.pause();
      } catch {
        // ignore
      }
      isPausedRef.current = true;
      setIsPaused(true);
      shouldBeRecordingRef.current = false;
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
        durationInterval.current = undefined;
      }
      return;
    }

    if (!recognitionRef.current) return;

    haptic.light();

    if (isPaused) {
      // Resume
      isPausedRef.current = false;
      resumeRequestedRef.current = true;
      setIsPaused(false);
      shouldBeRecordingRef.current = true;
      restartAttemptsRef.current = 0;

      try {
        recognitionRef.current.start();
      } catch {
        // Already started / not ready yet
      }

      // Timer is restarted in onstart (resume path)
      return;
    }

    // Pause
    isPausedRef.current = true;
    setIsPaused(true);
    shouldBeRecordingRef.current = false;

    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = undefined;
    }

    // stop() triggers onend; onend checks isPausedRef and won't teardown
    recognitionRef.current.stop();
  }, [haptic, isPaused]);

  const handleClick = useCallback(() => {
    if (disabled) return;

    if (isRecording) {
      stopRecording(false);
    } else {
      startRecording();
    }
  }, [disabled, isRecording, startRecording, stopRecording]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleClick();
    } else if (e.key === 'Escape' && isRecording) {
      e.preventDefault();
      stopRecording(true);
    }
  }, [handleClick, isRecording, stopRecording]);

  // Retry handler for failed transcription
  const handleRetry = useCallback(() => {
    setTranscriptionFailed(false);
    setTranscriptionProgress(0);
    startRecording();
  }, [startRecording]);

  // When transcription failed, show retry UI
  if (transcriptionFailed && !renderExternal) {
    return (
      <RecordingBar 
        duration={0}
        liveTranscript=""
        isPaused={false}
        onCancel={() => {
          setTranscriptionFailed(false);
        }}
        onSend={() => {}}
        onTogglePause={() => {}}
        transcriptionFailed={true}
        onRetry={handleRetry}
      />
    );
  }

  // When transcribing, show progress bar
  if (isTranscribing && !renderExternal) {
    return (
      <RecordingBar 
        duration={0}
        liveTranscript=""
        isPaused={false}
        onCancel={() => {
          // Allow cancelling transcription
          if (transcriptionTimeoutRef.current) {
            clearTimeout(transcriptionTimeoutRef.current);
            transcriptionTimeoutRef.current = null;
          }
          if (transcriptionProgressRef.current) {
            clearInterval(transcriptionProgressRef.current);
            transcriptionProgressRef.current = null;
          }
          setIsTranscribing(false);
          setTranscriptionProgress(0);
        }}
        onSend={() => {}}
        onTogglePause={() => {}}
        isTranscribing={true}
        transcriptionProgress={transcriptionProgress}
      />
    );
  }

  // When recording and NOT rendering externally, show RecordingBar as the full component
  if (isRecording && !renderExternal) {
    return (
      <RecordingBar 
        duration={recordingDuration}
        liveTranscript={liveTranscript}
        isPaused={isPaused}
        onCancel={() => stopRecording(true)}
        onSend={() => stopRecording(false)}
        onTogglePause={togglePause}
      />
    );
  }

  // Otherwise show the mic button
  return (
    <Button
      size="icon"
      variant="ghost"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`h-11 w-11 rounded-full shrink-0 transition-colors ${
        isRecording ? 'bg-destructive text-destructive-foreground' : ''
      }`}
      aria-label={isRecording ? "Stop recording" : "Start voice input"}
      aria-pressed={isRecording}
    >
      {isTranscribing ? <span className="text-xs">...</span> : <Mic className="w-5 h-5" />}
    </Button>
  );
};

// Hook for using voice recording in parent component
export const useVoiceRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  
  return {
    isRecording,
    setIsRecording,
  };
};
